import type { GeckoCase } from 'types'

import type * as MdCaseSchema from './mdCaseSchema'
import {
  createEmptyMdCaseFeedback as createEmptyMdCaseFeedbackSchema,
  createEmptyMdCaseFeedbackSection as createEmptyMdCaseFeedbackSectionSchema,
  isMdCaseRecord,
  MD_CASE_ALLOWED_TRANSITIONS,
  MD_CASE_PROMOTION_STATES as MD_CASE_PROMOTION_STATES_SCHEMA,
  MD_CASE_SEVERITIES as MD_CASE_SEVERITIES_SCHEMA,
  MD_CASE_SOURCE_TYPES as MD_CASE_SOURCE_TYPES_SCHEMA,
  MD_CASE_STATUSES as MD_CASE_STATUSES_SCHEMA,
  MD_CASE_WORKFLOW_DOMAIN as MD_CASE_WORKFLOW_DOMAIN_SCHEMA,
  MD_CASE_WORKFLOW_MODES as MD_CASE_WORKFLOW_MODES_SCHEMA,
  normalizeMdCaseRecord,
} from './mdCaseSchema'

export type MdCase = MdCaseSchema.MdCase
export type MdCaseAgent = MdCaseSchema.MdCaseAgent
export type MdCaseAlertItem = MdCaseSchema.MdCaseAlertItem
export type MdCaseAlerts = MdCaseSchema.MdCaseAlerts
export type MdCaseAnnotations = MdCaseSchema.MdCaseAnnotations
export type MdCaseFeedback = MdCaseSchema.MdCaseFeedback
export type MdCaseFeedbackSection = MdCaseSchema.MdCaseFeedbackSection
export type MdCaseFeedbackSections = MdCaseSchema.MdCaseFeedbackSections
export type MdCaseMeta = MdCaseSchema.MdCaseMeta
export type MdCaseNormalizationError = MdCaseSchema.MdCaseNormalizationError
export type MdCaseNormalizationResult = MdCaseSchema.MdCaseNormalizationResult
export type MdCaseNotes = MdCaseSchema.MdCaseNotes
export type MdCasePiTagReference = MdCaseSchema.MdCasePiTagReference
export type MdCasePromotionState = MdCaseSchema.MdCasePromotionState
export type MdCaseReferences = MdCaseSchema.MdCaseReferences
export type MdCaseSeverity = MdCaseSchema.MdCaseSeverity
export type MdCaseSourceType = MdCaseSchema.MdCaseSourceType
export type MdCaseStatus = MdCaseSchema.MdCaseStatus
export type MdCaseTransitionValidationInput =
  MdCaseSchema.MdCaseTransitionValidationInput
export type MdCaseTransitionValidationResult =
  MdCaseSchema.MdCaseTransitionValidationResult
export type MdCaseWorkflow = MdCaseSchema.MdCaseWorkflow
export type MdCaseWorkflowMode = MdCaseSchema.MdCaseWorkflowMode

export const MD_CASE_WORKFLOW_DOMAIN = MD_CASE_WORKFLOW_DOMAIN_SCHEMA
export const MD_CASE_WORKFLOW_MODES = MD_CASE_WORKFLOW_MODES_SCHEMA
export const MD_CASE_SOURCE_TYPES = MD_CASE_SOURCE_TYPES_SCHEMA
export const MD_CASE_SEVERITIES = MD_CASE_SEVERITIES_SCHEMA
export const MD_CASE_PROMOTION_STATES = MD_CASE_PROMOTION_STATES_SCHEMA
export const MD_CASE_STATUSES = MD_CASE_STATUSES_SCHEMA
export const createEmptyMdCaseFeedback = createEmptyMdCaseFeedbackSchema
export const createEmptyMdCaseFeedbackSection =
  createEmptyMdCaseFeedbackSectionSchema
export const isMdCase = isMdCaseRecord
export const normalizeMdCase = normalizeMdCaseRecord

const INVALID_TRANSITION_ERROR = 'Invalid M&D case transition'
const REOPEN_REASON_REQUIRED_ERROR = 'Reopening an M&D case requires a reason'

const parseNullableString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmedValue = value.trim()
  return trimmedValue.length > 0 ? trimmedValue : null
}

export const isMdCaseStatus = (value: unknown): value is MdCaseStatus =>
  typeof value === 'string' && MD_CASE_STATUSES.includes(value as MdCaseStatus)

export const toMdCase = (caseEntry: GeckoCase | null | undefined) => {
  const result = normalizeMdCaseRecord(caseEntry)
  return result.isMdCase && result.hasCoreFields ? result.value : null
}

export const canTransitionMdCaseStatus = (
  from: MdCaseStatus,
  to: MdCaseStatus
): boolean => MD_CASE_ALLOWED_TRANSITIONS[from].includes(to)

export const validateMdCaseTransition = ({
  from,
  to,
  reopenReason,
}: MdCaseTransitionValidationInput): MdCaseTransitionValidationResult => {
  const normalizedReopenReason = parseNullableString(reopenReason)
  const requiresReopenReason =
    to === 'Open' && (from === 'Closed' || from === 'Cleared')

  if (!canTransitionMdCaseStatus(from, to)) {
    return {
      isValid: false,
      requiresReopenReason,
      normalizedReopenReason,
      error: `${INVALID_TRANSITION_ERROR}: ${from} -> ${to}`,
    }
  }

  if (requiresReopenReason && normalizedReopenReason === null) {
    return {
      isValid: false,
      requiresReopenReason: true,
      normalizedReopenReason: null,
      error: REOPEN_REASON_REQUIRED_ERROR,
    }
  }

  return {
    isValid: true,
    requiresReopenReason,
    normalizedReopenReason: requiresReopenReason
      ? normalizedReopenReason
      : null,
    error: null,
  }
}
